/**
 * useShouldBeTransparent Hook
 *
 * Prüft, ob eine Karte von CMC-Filter, Syntax-Suche oder Textsuche ausgeschlossen ist.
 *
 * Alle vier Views nutzen das Ergebnis als Filter — ausgeschlossene Karten werden aus der
 * Liste entfernt, nicht gedimmt. Die Views bilden ihr `isFilterActive` aus denselben drei
 * Bedingungen; ein gedimmter Zustand ist damit nicht erreichbar.
 */
import { useCallback } from 'react';
import { splitSearchWords, textContainsAllWords } from '../../../utils/wordSearch';

/**
 * @param {Object} options
 * @param {number|null} options.highlightedCmc - Aktuell hervorgehobene CMC (null = kein Filter)
 * @param {string} options.searchTerm - Aktueller Suchbegriff
 * @param {Set|null} options.matchingCardIds - Set von Card-IDs bei Syntax-Suche (null = keine Syntax-Suche)
 * @returns {Function} shouldBeTransparent(card) → boolean
 */
export function useShouldBeTransparent({ highlightedCmc, searchTerm, matchingCardIds }) {
  return useCallback((card) => {
    // CMC-Filter
    if (highlightedCmc !== null) {
      const cardCmc = Math.min(Math.floor(card.cmc || 0), 7);
      const targetCmc = highlightedCmc >= 7 ? 7 : highlightedCmc;
      if (cardCmc !== targetCmc) return true;
    }
    
    // Syntax-Suche: matchingCardIds ist ein Set mit passenden IDs
    if (matchingCardIds !== null) {
      // Prüfe mit card.id (numerisch) und card.scryfallId
      const matches = matchingCardIds.has(card.id) || 
                      matchingCardIds.has(String(card.id)) ||
                      (card.scryfallId && matchingCardIds.has(card.scryfallId));
      if (!matches) return true;
    }
    // Normale Textsuche (nur wenn keine Syntax-Suche aktiv) — wortweise UND
    else if (searchTerm && searchTerm.trim()) {
      const words = splitSearchWords(searchTerm);
      if (!textContainsAllWords(card.name || '', words)) return true;
    }
    
    return false;
  }, [highlightedCmc, searchTerm, matchingCardIds]);
}
